import { updateProfile } from 'flow/actions/profile-actions';
import Loading from 'flow/components/ui-components/Loading';
import Divider from 'flow/components/ui-components/Divider';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import RadioButton from 'flow/components/ui-components/RadioButton';
import Toggle from 'flow/components/ui-components/Toggle';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';

import { TypedMap } from 'flow/types/TypedMap';
import { homeActionUrlPath } from 'flow/utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import { hasSpokenReferralActiveOnProfile } from 'flow/utils/profile-utils';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import { List } from 'immutable';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useParams } from 'react-router-dom';
import ReferralNumberCombobox, {
    ReferralNumberOption,
} from '../../components/ReferralNumberCombobox';
import ReferralSoundCombobox, {
    SoundOption,
} from '../../components/ReferralSoundCombobox';
import Page from '../../Page';
import SettingRow from '../../SettingRow';
import SettingSection from '../../SettingSection';
import formatDuration from '../functions/formatDuration';
import getReferralOptions from '../functions/getReferralOptions';
import getReferralType from '../functions/getReferralType';
import onUpdate from '../functions/onUpdate';
import {
    buildReferralSoundUpdate,
    getForwardingNumberUpdates,
    getReferralTypeUpdates,
} from '../functions/referralUpdates';

interface Props {
    profile: TypedMap<Profile>;
    onClickBack: MouseEventHandler;
}

const OtherLandline = ({ profile, onClickBack }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { ownContact } = useContact();
    const initialDelays = [5, 10, 15, 20, 25, 30, 45, 60, 90, 120];

    const referralSounds = useAppSelector((state) =>
        state.profiles.get('referal_sounds', List()),
    );

    const [referralSound, setReferralSound] = useState<SoundOption>();
    const [showReferralNumberCombobox, setShowReferralNumberCombobox] =
        useState(false);

    const [selectedType, setSelectedType] = useState('');
    const [referralNumber, setReferralNumber] = useState<ReferralNumberOption>({
        dto: {},
        label: null,
    });

    useEffect(() => {
        if (profile.hasIn(['spokenNotification', 'referEntry', 'sound'])) {
            setReferralSound({
                id: profile.getIn(['spokenNotification', 'referEntry', 'key']),
                name: profile.getIn([
                    'spokenNotification',
                    'referEntry',
                    'description',
                ]),
                playbackUrl: profile.hasIn([
                    'spokenNotification',
                    'referEntry',
                    'sound',
                ])
                    ? homeActionUrlPath(
                          '/' +
                              profile.getIn([
                                  'spokenNotification',
                                  'referEntry',
                                  'sound',
                                  'playbackURL',
                              ]),
                      )
                    : null,
            });
        } else {
            setReferralSound({
                id: null,
                name: i18next.t('settings.incoming_calls.profile.message.none'),
                playbackUrl: null,
            });
        }

        setReferralNumber({
            label: profile.getIn([
                'callForwarding',
                'redirect',
                'nationalFormat',
            ]),
            dto: {},
        });
    }, [profile]);

    const countryCode = getCountryCode(ownContact, null);

    if (!referralSounds || !countryCode) {
        return <Loading />;
    }

    const referAction = profile.getIn([
        'spokenNotification',
        'referPhraseType',
    ]);

    const unconditionalRedirectType = profile.getIn([
        'callForwarding',
        'unconditionalRedirect',
        'type',
    ]);

    const parallelCallType = profile.getIn([
        'parallelCalls',
        'parallelCallType',
    ]);

    const sound = referralSounds.find(
        (sound) =>
            sound.get('key') ===
            profile.getIn(['spokenNotification', 'referEntry', 'key']),
    );

    const displayComboBox =
        showReferralNumberCombobox || unconditionalRedirectType === 'number';

    const onChangeReferralSound = (sound: SoundOption) => {
        const referEntry = referralSounds.find(
            (refer) => refer.get('key') === sound.id,
        );
        dispatch(
            updateProfile(
                extensionKey,
                buildReferralSoundUpdate(profile, referEntry),
            ),
        );
    };

    const getRedirectType = () =>
        selectedType === 'number' ? 'number' : getReferralType(profile);

    return (
        <Page onClickBack={onClickBack} header="settings.incoming_calls.fixed">
            <SettingSection>
                {i18next.t('settings.incoming_calls.profile.message')}
            </SettingSection>

            <ReferralSoundCombobox
                ariaLabel={i18next.t('settings.incoming_calls.profile.message')}
                sound={referralSound}
                onChange={onChangeReferralSound}
            />

            <Divider />

            <div className={!sound ? 'opacity-50' : ''}>
                <SettingSection>
                    {i18next.t('settings.incoming_calls.profile.endtime.title')}
                </SettingSection>

                <SettingRow
                    cta={
                        <Toggle
                            disabled={!sound}
                            value={profile.getIn([
                                'spokenNotification',
                                'announceReferUntilTime',
                            ])}
                            onChange={(value) =>
                                dispatch(
                                    onUpdate(extensionKey, profile.get('key'), [
                                        {
                                            state: [
                                                'spokenNotification',
                                                'announceReferUntilTime',
                                            ],
                                            value,
                                        },
                                    ]),
                                )
                            }
                            text={i18next.t(
                                'settings.incoming_calls.profile.endtime',
                            )}
                        />
                    }
                >
                    {i18next.t('settings.incoming_calls.profile.endtime')}
                </SettingRow>
            </div>

            <Divider />

            <div className={!sound ? 'opacity-50' : ''}>
                <SettingSection>
                    {i18next.t('settings.incoming_calls.profile.greeting')}
                </SettingSection>

                <DropdownMenuComponent
                    disabled={!sound}
                    title={i18next.t(
                        `settings.incoming_calls.profile.greeting.${referAction}`,
                    )}
                    onItemSelected={(value) =>
                        dispatch(
                            onUpdate(extensionKey, profile.get('key'), [
                                {
                                    state: [
                                        'spokenNotification',
                                        'referPhraseType',
                                    ],
                                    value,
                                },
                            ]),
                        )
                    }
                    options={['firstname', 'both', 'standard'].map((key) => ({
                        key,
                        label: i18next.t(
                            `settings.incoming_calls.profile.greeting.${key}`,
                        ),
                    }))}
                />
            </div>

            <Divider />

            <SettingSection>
                {i18next.t('settings.incoming_calls.profile.referal')}
            </SettingSection>

            <DropdownMenuComponent
                title={i18next.t(
                    `settings.incoming_calls.profile.referal.${getRedirectType()}`,
                )}
                onItemSelected={(type) => {
                    setSelectedType(type);
                    if (type === 'number') {
                        setShowReferralNumberCombobox(true);
                        return;
                    }
                    dispatch(
                        onUpdate(
                            extensionKey,
                            profile.get('key'),
                            getReferralTypeUpdates(type),
                        ),
                    );

                    setShowReferralNumberCombobox(false);
                }}
                options={getReferralOptions(
                    hasSpokenReferralActiveOnProfile(profile),
                ).map((key) => ({
                    key,
                    label: i18next.t(
                        `settings.incoming_calls.profile.referal.${key}`,
                    ),
                }))}
            />

            {displayComboBox && (
                <div className="mt-4">
                    <ReferralNumberCombobox
                        ariaLabel={i18next.t(
                            'settings.callrouting.action.picknumber',
                        )}
                        referralNumber={referralNumber}
                        onChange={(option) => {
                            setReferralNumber(option);
                            dispatch(
                                onUpdate(
                                    extensionKey,
                                    profile.get('key'),
                                    getForwardingNumberUpdates(option.dto),
                                ),
                            );
                        }}
                    />
                </div>
            )}
            <Divider />
            {selectedType !== 'number' &&
                parallelCallType === 'both' &&
                unconditionalRedirectType === 'busy' && (
                    <>
                        <SettingSection>
                            {i18next.t(
                                'settings.incoming_calls.optionlist.delay',
                            )}
                        </SettingSection>

                        {profile
                            .getIn(['parallelCalls', 'delayTypeOptions'])
                            .map((delayTypeOption) => {
                                const getDelayTitle = () => {
                                    if (delayTypeOption === 'no_delay') {
                                        return i18next.t(
                                            'settings.incoming_calls.delay.none',
                                        );
                                    }

                                    if (delayTypeOption === 'mobile') {
                                        return i18next.t(
                                            'settings.incoming_calls.optionlist.delay_mobile',
                                        );
                                    }

                                    return i18next.t(
                                        'settings.incoming_calls.optionlist.delay_landline',
                                    );
                                };

                                return (
                                    <RadioButton
                                        key={`delayTypeOptions-${delayTypeOption}`}
                                        name="delayTypeOptions"
                                        checked={
                                            profile.getIn([
                                                'parallelCalls',
                                                'delayType',
                                            ]) === delayTypeOption
                                        }
                                        onChange={() =>
                                            dispatch(
                                                onUpdate(
                                                    extensionKey,
                                                    profile.get('key'),
                                                    [
                                                        {
                                                            state: [
                                                                'parallelCalls',
                                                                'delayType',
                                                            ],
                                                            value: delayTypeOption,
                                                        },
                                                        {
                                                            state: [
                                                                'parallelCalls',
                                                                'mexDelay',
                                                            ],
                                                            value: 5,
                                                        },
                                                    ],
                                                ),
                                            )
                                        }
                                    >
                                        {getDelayTitle()}
                                    </RadioButton>
                                );
                            })}

                        {profile.getIn(['parallelCalls', 'delayType']) !==
                            'no_delay' && (
                            <DropdownMenuComponent
                                title={`${i18next.t(
                                    'settings.incoming_calls.optionlist.delay_time',
                                )} ( ${formatDuration(
                                    profile.getIn([
                                        'parallelCalls',
                                        'mexDelay',
                                    ]),
                                )} )`}
                                options={initialDelays.map((item) => ({
                                    key: item.toString(),
                                    label: formatDuration(item),
                                }))}
                                onItemSelected={(value) =>
                                    dispatch(
                                        onUpdate(
                                            extensionKey,
                                            profile.get('key'),
                                            [
                                                {
                                                    state: [
                                                        'parallelCalls',
                                                        'mexDelay',
                                                    ],
                                                    value,
                                                },
                                            ],
                                        ),
                                    )
                                }
                            />
                        )}
                    </>
                )}
            {selectedType !== 'number' &&
                unconditionalRedirectType === 'busy' &&
                (parallelCallType === 'fixed' ||
                    parallelCallType === 'both') && (
                    <>
                        <SettingSection>
                            {i18next.t('settings.advanced')}
                        </SettingSection>

                        <SettingRow
                            information={i18next.t(
                                'settings.incoming_calls_mvoip.warning',
                            )}
                            cta={
                                <Toggle
                                    value={profile.getIn(['callMVoip'])}
                                    onChange={(value) =>
                                        dispatch(
                                            onUpdate(
                                                extensionKey,
                                                profile.get('key'),
                                                [
                                                    {
                                                        state: ['callMVoip'],
                                                        value,
                                                    },
                                                ],
                                            ),
                                        )
                                    }
                                    text={i18next.t(
                                        'settings.incoming_calls.mvoip',
                                    )}
                                />
                            }
                        >
                            {i18next.t('settings.incoming_calls.mvoip')}
                        </SettingRow>
                    </>
                )}
        </Page>
    );
};

export default OtherLandline;
